import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { EVENT, makeCustomEvent } from '../../../util/events';
import { consume } from '@lit/context';
import { formDataContext } from '../../../contexts/formData';
import { RateQuoteFormData, ENROLLMENT_TO_SELF_QUESTION } from '../../../util/form';
import '../../../components/continue-button';
import { sourceContext } from '../../../contexts/source';
import { SOURCE } from '../../../util/source';
import { breakpointContext } from '../../../contexts/breakpoint';
import { BREAKPOINT } from '../../../util/breakpoints';
import { CURRENT_YEAR, NEXT_YEAR, SWITCH_ON_AEP } from '../../../util/config';
const LIFE_CHANGES_URL = 'https://www.bluecrossnc.com/blog/insurance-basics/insurance-coverage-with-life-changes';

type LitehouseSelectElement = HTMLElement & {
    value: string;
    change: (value: string) => void;
};

type LitehouseRadioGroupElement = HTMLElement & {
    value: string;
    change: (value: string) => void;
};

const { VITE_BCBSNC_URL } = import.meta.env;
const SESSION_CHECK_PATH = '/sapps/buyonline/application/SessionCheck.action';

const ENROLLMENT_FOR_OPTIONS = [
    { value: 'myself', label: 'For myself only' },
    { value: 'myself-family', label: 'For myself, spouse, and / or dependent' },
    { value: 'dependent-over-18', label: 'For a dependent who is over 18' },
    { value: 'dependent-under-18', label: 'For a dependent who is under 18' },
    { value: 'someone-else-legal', label: 'For someone else and I have legal representation' },
    { value: 'someone-else-no-legal', label: "For someone else and I don't have legal representation / guardianship" },
];

const NEEDS_SESSION_CHECK_VALUES = [
    'dependent-over-18',
    'dependent-under-18',
    'someone-else-legal',
    'someone-else-no-legal',
];

@customElement('rq-step-start')
export class RateQuoteStart extends LitElement {
    static styles = css`
        .wrapper {
            display: flex;
            flex-direction: column;
            gap: 24px;
            padding-bottom: 100px;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('lh-change', this.listenerHandler as EventListener);
        this.checkActiveSession();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('lh-change', this.listenerHandler as EventListener);
    }

    @consume({ context: formDataContext, subscribe: true })
    data!: RateQuoteFormData;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: sourceContext })
    source!: SOURCE;

    @state()
    private hasActiveSession: boolean | null = null;

    @state()
    private sessionCheckDone = false;

    private lastSyncedEnrollmentFor: string | undefined = undefined;
    private lastSyncedQualifyingEvent: boolean | string | undefined = undefined;
    private lastSyncedCoverageStart: string | undefined = undefined;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    updated(changedProps: Map<string | number | symbol, unknown>) {
        super.updated(changedProps);
        // Sync form values only if they've changed (not on every render)
        if (this.data.enrollmentFor && this.data.enrollmentFor !== this.lastSyncedEnrollmentFor) {
            const enrollmentSelect = this.shadowRoot?.querySelector(
                'lh-select[name="enrollmentFor"]'
            ) as LitehouseSelectElement | null;
            if (enrollmentSelect?.change && enrollmentSelect.value !== this.data.enrollmentFor) {
                enrollmentSelect.change(this.data.enrollmentFor);
                this.lastSyncedEnrollmentFor = this.data.enrollmentFor;
            }
        }
        // Update qualifyingEvent radio group
        if (this.data.qualifyingEvent !== undefined && this.data.qualifyingEvent !== this.lastSyncedQualifyingEvent) {
            const qualifyingRadioGroup = this.shadowRoot?.querySelector(
                'lh-radio-group[name="qualifyingEvent"]'
            ) as LitehouseRadioGroupElement | null;
            if (qualifyingRadioGroup?.change) {
                const newValue = String(this.data.qualifyingEvent);
                if (qualifyingRadioGroup.value !== newValue) {
                    qualifyingRadioGroup.change(newValue);
                    this.lastSyncedQualifyingEvent = this.data.qualifyingEvent;
                }
            }
        }
        // Update coverageStart radio group
        if (this.data.coverageStart && this.data.coverageStart !== this.lastSyncedCoverageStart) {
            const coverageRadioGroup = this.shadowRoot?.querySelector(
                'lh-radio-group[name="coverageStart"]'
            ) as LitehouseRadioGroupElement | null;
            if (coverageRadioGroup?.change && coverageRadioGroup.value !== this.data.coverageStart) {
                coverageRadioGroup.change(this.data.coverageStart);
                this.lastSyncedCoverageStart = this.data.coverageStart;
            }
        }
    }

    private listenerHandler({
        detail: { name, value },
    }: CustomEvent<{ name: keyof RateQuoteFormData; value: unknown }>) {
        const updatedForm: RateQuoteFormData = {
            ...this.data,
            [name]: value,
        };

        // Auto-sync enrollmentFor to selfQuestion
        if (name === 'enrollmentFor' && typeof value === 'string') {
            updatedForm.selfQuestion = ENROLLMENT_TO_SELF_QUESTION[value] || '';
            // Also store in sessionStorage as backup
            sessionStorage.setItem('rq_selfQuestion', updatedForm.selfQuestion);
        }

        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));

        if (name === 'enrollmentFor' && NEEDS_SESSION_CHECK_VALUES.includes(value as string)) {
            this.checkActiveSession();
        } else if (name === 'enrollmentFor') {
            this.hasActiveSession = null;
            this.sessionCheckDone = false;
        }
    }

    private async checkActiveSession() {
        this.sessionCheckDone = false;

        const sessionUrl = `${VITE_BCBSNC_URL}${SESSION_CHECK_PATH}`;

        this.hasActiveSession = await new Promise<boolean>((resolve) => {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';

            let settled = false;

            const cleanup = () => {
                window.removeEventListener('message', onMessage);
                window.clearTimeout(timeoutId);
                iframe.remove();
            };

            const finish = (isActive: boolean) => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                resolve(isActive);
            };

            const onMessage = (event: MessageEvent) => {
                const isLoggedIn =
                    typeof event.data === 'object' && event.data !== null
                        ? (event.data as { isBuyonlineLoggedIn?: boolean }).isBuyonlineLoggedIn
                        : undefined;

                if (isLoggedIn === true) {
                    finish(true);
                }

                if (isLoggedIn === false) {
                    finish(false);
                }
            };

            const timeoutId = window.setTimeout(() => {
                finish(false);
            }, 5000);

            window.addEventListener('message', onMessage);

            iframe.onerror = () => {
                finish(false);
            };

            iframe.src = sessionUrl;
            document.body.appendChild(iframe);
        });

        this.sessionCheckDone = true;
    }

    private async handleLogout() {
        try {
            const logoutUrl = `${VITE_BCBSNC_URL}/sapps/buyonline/application/OnlineApplicationControl_logout.action`;
            await new Promise<void>((resolve, reject) => {
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.style.position = 'absolute';
                iframe.style.width = '0';
                iframe.style.height = '0';

                const cleanup = () => {
                    window.clearTimeout(timeoutId);
                    iframe.remove();
                };

                const timeoutId = window.setTimeout(() => {
                    cleanup();
                    reject(new Error('Logout iframe timed out'));
                }, 5000);

                iframe.onload = () => {
                    cleanup();
                    resolve();
                };

                iframe.onerror = () => {
                    cleanup();
                    reject(new Error('Logout iframe failed'));
                };

                iframe.src = logoutUrl;
                document.body.appendChild(iframe);
            });
            this.hasActiveSession = false;
        } catch {
            window.location.href = `${VITE_BCBSNC_URL}/sapps/buyonline/application/OnlineApplicationControl_logout.action`;
        }
    }

    get disableContinue() {
        const { coverageStart, qualifyingEvent, enrollmentFor } = this.data;
        // Require enrollmentFor for direct shoppers - check for empty string, null, or undefined
        if (this.source === SOURCE.DIRECT_SHOPPER && (!enrollmentFor || enrollmentFor === '')) return true;

        if (SWITCH_ON_AEP) {
            if (!coverageStart) return true;
            if (
                coverageStart === CURRENT_YEAR &&
                coverageStart !== NEXT_YEAR &&
                (!qualifyingEvent || qualifyingEvent === 'false')
            ) {
                return true;
            } else {
                // Even if other conditions pass, still check enrollmentFor for direct shoppers
                if (this.source === SOURCE.DIRECT_SHOPPER && (!enrollmentFor || enrollmentFor === '')) return true;
                return false;
            }
        }
        if (!SWITCH_ON_AEP && !qualifyingEvent) return true;
        return qualifyingEvent === 'false';
    }

    get nonAepMessage() {
        return html` <lh-typography> First, we'll make sure you're eligible to start a new plan. </lh-typography> `;
    }

    get aepCoverageStartInput() {
        const coverageStart =
            this.data.coverageStart && /^\d{2}\/\d{2}\/\d{4}$/.test(this.data.coverageStart)
                ? this.data.coverageStart.split('/')[2]
                : this.data.coverageStart;

        return html` <lh-typography> First, let us know when you’d like your new plan to become active. </lh-typography>
            <lh-radio-group required name="coverageStart" default-value=${ifDefined(coverageStart)}>
                <lh-group-legend slot="legend"
                    >Are you shopping for a ${NEXT_YEAR} plan as part of open enrollment?</lh-group-legend
                >
                <lh-radio value="${NEXT_YEAR}">Yes</lh-radio>
                <lh-radio value="${CURRENT_YEAR}">No</lh-radio>
            </lh-radio-group>`;
    }

    get qualifyingEventWarning() {
        if (this.data.qualifyingEvent === 'false') {
            return html`<lh-alert variant="warning" inline>
                You must have a Qualifying Life Event within 60 days to add or change your coverage. If you have not had
                one, you will need to wait until Open Enrollment to add a plan or change your plan.
            </lh-alert>`;
        }
        return nothing;
    }

    get qualifyingAEPEventWarning() {
        if (this.data.qualifyingEvent === 'false' && this.data.coverageStart !== `${NEXT_YEAR}`) {
            return html`<lh-alert variant="warning" inline>
                For coverage to start now, you must have a recent qualifying life event. Otherwise, you can shop for a
                plan that will start next year.
            </lh-alert>`;
        }
        return nothing;
    }

    get needsSessionCheck() {
        return NEEDS_SESSION_CHECK_VALUES.includes(this.data.enrollmentFor || '');
    }

    get enrollmentForYellowWarning() {
        if (!this.needsSessionCheck || !this.sessionCheckDone || this.hasActiveSession) return nothing;
        return html`<lh-alert variant="warning" inline>
            <lh-typography
                >You can only apply for yourself or someone else you’re legally authorized to represent.</lh-typography
            >
            <lh-typography spacing-top="sm"
                >To apply for someone else, create an account in their name after selecting a plan.</lh-typography
            >
        </lh-alert>`;
    }

    get enrollmentForBlueWarning() {
        if (!this.needsSessionCheck || !this.sessionCheckDone || !this.hasActiveSession) return nothing;
        return html`<lh-alert variant="info" inline>
            <lh-typography
                >You can only apply for yourself or someone else you’re legally authorized to represent.</lh-typography
            >

            <lh-typography
                >To apply for this person, log out and create an account in their name to begin their enrollment
                application.</lh-typography
            >

            <lh-typography>
                <lh-button variant="text" icon="caret-right" @click=${this.handleLogout}> Log out</lh-button>
            </lh-typography>
        </lh-alert>`;
    }

    get enrollmentForQuestion() {
        if (this.source !== SOURCE.DIRECT_SHOPPER) return nothing;
        return html`
            <lh-select required name="enrollmentFor">
                Who are you completing this enrollment application for?
                <lh-option slot="options" value="" ?default-selected=${!this.data.enrollmentFor}>Select one</lh-option>
                ${ENROLLMENT_FOR_OPTIONS.map(
                    (opt) =>
                        html`<lh-option
                            slot="options"
                            value=${opt.value}
                            ?default-selected=${this.data.enrollmentFor === opt.value}
                            >${opt.label}</lh-option
                        >`
                )}
            </lh-select>
            ${this.enrollmentForYellowWarning} ${this.enrollmentForBlueWarning}
        `;
    }

    get qualifyingEventGroup() {
        return html`
            <lh-radio-group required name="qualifyingEvent" default-value=${String(this.data.qualifyingEvent)}>
                <lh-group-legend slot="legend">
                    Have you had a
                    <lh-button
                        variant="text"
                        href=${LIFE_CHANGES_URL}
                        target="_blank"
                        analytics-id="rate-quote:${this.source}:u65:step1:qle:link"
                    >
                        qualifying life event
                    </lh-button>
                    in the last 60 days?
                </lh-group-legend>
                <lh-group-description slot="description">
                    Common qualifying life events include having a baby, getting married, or moving to a new place.
                </lh-group-description>
                <lh-radio value="true">Yes</lh-radio>
                <lh-radio value="false">No</lh-radio>
            </lh-radio-group>
        `;
    }

    render() {
        return html`
            <div class="wrapper">
                <lh-typography type="heading" selector="h5" size=${this.isMobile ? 'xxl' : 'lg'}>
                    Let's start your quote
                </lh-typography>
                ${SWITCH_ON_AEP ? this.aepCoverageStartInput : this.nonAepMessage}
                ${SWITCH_ON_AEP
                    ? this.data.coverageStart === CURRENT_YEAR
                        ? this.qualifyingEventGroup
                        : nothing
                    : this.qualifyingEventGroup}
                ${SWITCH_ON_AEP ? this.qualifyingAEPEventWarning : this.qualifyingEventWarning}
                ${this.enrollmentForQuestion}
                <rq-continue-button
                    analytics-id="rate-quote:${this.source}:u65:step1:continue:button-filled"
                    .disabled=${this.disableContinue}
                ></rq-continue-button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-step-start': RateQuoteStart;
    }
}
